import React, { useState, useEffect } from 'react';
import { Download, X, Smartphone, Chrome, Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Hook para detectar se o app pode ser instalado
export const usePWAInstall = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Listen for the beforeinstallprompt event
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    // Listen for app installed event
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const installApp = async () => {
    if (!deferredPrompt) return false;
    
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setDeferredPrompt(null);
      setIsInstallable(false);
      return true;
    }
    return false;
  };

  return { isInstallable, isInstalled, installApp, deferredPrompt };
};

// Botão grande de download para a hero section - INSTALA DIRETO NO ANDROID
export const DownloadAppButton = () => {
  const { isInstallable, isInstalled, installApp, deferredPrompt } = usePWAInstall();
  const [showInstructions, setShowInstructions] = useState(false);

  // Detect platform
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isAndroid = /Android/.test(navigator.userAgent);

  // Se já instalou, mostra badge de sucesso
  if (isInstalled) {
    return (
      <div className="flex items-center gap-2 px-6 py-3 rounded-full bg-[#00FF94]/10 text-[#00FF94] border border-[#00FF94]/30">
        <Smartphone className="w-5 h-5" />
        <span className="font-semibold">App instalado!</span>
      </div>
    );
  }

  const handleDownload = async () => {
    // Se pode instalar direto (Android com Chrome), instala na hora
    if (deferredPrompt && isInstallable) {
      await installApp();
      return;
    }
    
    // Senão, mostra instruções
    setShowInstructions(true);
  };

  return (
    <>
      <button
        onClick={handleDownload}
        className="flex items-center gap-3 px-8 py-4 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#00FF94]/50 rounded-2xl transition-all group"
        data-testid="download-app-hero-btn"
      >
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#00FF94] to-[#00CC76] flex items-center justify-center shadow-lg shadow-[#00FF94]/20 group-hover:scale-110 transition-transform">
          <Download className="w-6 h-6 text-black" />
        </div>
        <div className="text-left">
          <p className="text-white font-bold text-lg">Baixar o App</p>
          <p className="text-gray-400 text-sm">Grátis • Funciona offline</p>
        </div>
      </button>

      {/* Instructions Modal */}
      {showInstructions && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80">
          <div className="bg-[#0A0A0A] rounded-2xl p-6 max-w-md w-full border border-white/10 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <h4 className="text-xl font-bold text-white">
                {isIOS ? 'Como instalar no iPhone' : isAndroid ? 'Como instalar no Android' : 'Como instalar o App'}
              </h4>
              <button 
                onClick={() => setShowInstructions(false)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            {isIOS ? (
              <ol className="space-y-4 text-gray-300">
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#00FF94]/20 text-[#00FF94] flex items-center justify-center text-sm font-bold">1</span>
                  <span>Abra este site no <strong className="text-white">Safari</strong></span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#00FF94]/20 text-[#00FF94] flex items-center justify-center text-sm font-bold">2</span>
                  <div className="flex items-center gap-2">
                    <span>Toque em </span>
                    <Share2 className="w-5 h-5 text-[#00FF94]" />
                    <span><strong className="text-white">Compartilhar</strong></span>
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#00FF94]/20 text-[#00FF94] flex items-center justify-center text-sm font-bold">3</span>
                  <span>Role e toque em <strong className="text-white">"Adicionar à Tela de Início"</strong></span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#00FF94]/20 text-[#00FF94] flex items-center justify-center text-sm font-bold">4</span>
                  <span>Toque em <strong className="text-white">"Adicionar"</strong></span>
                </li>
              </ol>
            ) : isAndroid ? (
              <ol className="space-y-4 text-gray-300">
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#00FF94]/20 text-[#00FF94] flex items-center justify-center text-sm font-bold">1</span>
                  <div className="flex items-center gap-2">
                    <span>Abra no <strong className="text-white">Chrome</strong></span>
                    <Chrome className="w-5 h-5 text-[#00FF94]" />
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#00FF94]/20 text-[#00FF94] flex items-center justify-center text-sm font-bold">2</span>
                  <span>Toque nos <strong className="text-white">3 pontos</strong> (⋮) no canto superior direito</span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#00FF94]/20 text-[#00FF94] flex items-center justify-center text-sm font-bold">3</span>
                  <span>Toque em <strong className="text-white">"Instalar aplicativo"</strong></span>
                </li>
              </ol>
            ) : (
              <ol className="space-y-4 text-gray-300">
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#00FF94]/20 text-[#00FF94] flex items-center justify-center text-sm font-bold">1</span>
                  <span>Abra no <strong className="text-white">Chrome</strong> ou <strong className="text-white">Edge</strong></span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#00FF94]/20 text-[#00FF94] flex items-center justify-center text-sm font-bold">2</span>
                  <span>Procure o ícone de <strong className="text-white">instalar</strong> (⊕) na barra de endereço</span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#00FF94]/20 text-[#00FF94] flex items-center justify-center text-sm font-bold">3</span>
                  <span>Clique e confirme a instalação</span>
                </li>
              </ol>
            )}

            <Button
              onClick={() => setShowInstructions(false)}
              className="w-full mt-6 bg-[#00FF94] text-black hover:bg-[#00CC76]"
            >
              Entendi!
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

// Botão de instalação para o header - SEMPRE VISÍVEL
export const InstallButton = ({ className = '' }) => {
  const { isInstallable, isInstalled, installApp, deferredPrompt } = usePWAInstall();
  const [showTip, setShowTip] = useState(false);

  // Detect platform
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isAndroid = /Android/.test(navigator.userAgent);

  // Se já instalou, não mostra
  if (isInstalled) return null;

  const handleClick = async () => {
    // Se pode instalar direto, instala
    if (deferredPrompt && isInstallable) {
      await installApp();
      return;
    }
    
    // Senão, mostra dica rápida
    setShowTip(true);
    setTimeout(() => setShowTip(false), 5000);
  };

  const getTip = () => {
    if (isIOS) return "No Safari: toque em Compartilhar → Adicionar à Tela de Início";
    if (isAndroid) return "No Chrome: toque em ⋮ → Adicionar à tela inicial";
    return "No Chrome: clique em ⊕ na barra de endereço";
  };

  return (
    <div className="relative">
      <Button
        variant="ghost"
        className={`text-[#00FF94] hover:text-white hover:bg-[#00FF94]/10 text-sm ${className}`}
        onClick={handleClick}
        data-testid="install-app-btn"
      >
        <Download className="w-4 h-4 mr-1 sm:mr-2" />
        <span>Baixar</span>
      </Button>
      
      {showTip && (
        <div className="absolute top-full right-0 mt-2 w-64 p-3 bg-[#0A0A0A] border border-[#00FF94]/30 rounded-lg shadow-lg z-50 text-sm text-gray-300">
          <p>{getTip()}</p>
          <button 
            onClick={() => setShowTip(false)}
            className="absolute top-1 right-1 text-gray-500 hover:text-white"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

// Seção de download para a landing page
export const DownloadAppSection = () => {
  const { isInstallable, isInstalled, installApp, deferredPrompt } = usePWAInstall();
  const [showInstructions, setShowInstructions] = useState(false);

  // Detect platform
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isAndroid = /Android/.test(navigator.userAgent);
  const isMobile = isIOS || isAndroid;

  const handleDownload = async () => {
    // If we have the install prompt available, use it
    if (deferredPrompt && isInstallable) {
      const installed = await installApp();
      if (installed) return;
    }
    
    // Otherwise show instructions
    setShowInstructions(true);
  };

  if (isInstalled) {
    return (
      <div className="text-center py-8">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#00FF94]/10 text-[#00FF94]">
          <Smartphone className="w-5 h-5" />
          <span>App instalado!</span>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="bg-gradient-to-br from-[#0A0A0A] to-[#0F1F15] rounded-2xl p-8 border border-[#00FF94]/20">
        <div className="flex flex-col md:flex-row items-center gap-8">
          {/* App Icon */}
          <div className="flex-shrink-0">
            <div className="w-24 h-24 md:w-32 md:h-32 rounded-2xl overflow-hidden shadow-lg shadow-[#00FF94]/20 bg-black">
              <img 
                src="/logo-192.png" 
                alt="Evite Dívidas App" 
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 text-center md:text-left">
            <h3 className="text-2xl font-bold text-white mb-2">
              Baixe o App Evite Dívidas
            </h3>
            <p className="text-gray-400 mb-4">
              Tenha acesso rápido às análises financeiras direto do seu celular. 
              Funciona offline e ocupa pouco espaço.
            </p>
            
            <div className="flex flex-wrap gap-3 justify-center md:justify-start">
              <Button
                onClick={handleDownload}
                className="bg-[#00FF94] text-black hover:bg-[#00CC76] rounded-full font-semibold px-6 py-5"
                data-testid="download-app-section-btn"
              >
                <Download className="w-5 h-5 mr-2" />
                {isInstallable ? 'Instalar Agora' : 'Como Instalar'}
              </Button>
            </div>

            {/* Features */}
            <div className="flex flex-wrap gap-4 mt-6 justify-center md:justify-start">
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <div className="w-2 h-2 rounded-full bg-[#00FF94]" />
                Grátis
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <div className="w-2 h-2 rounded-full bg-[#00FF94]" />
                Funciona offline
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <div className="w-2 h-2 rounded-full bg-[#00FF94]" />
                Sem ocupar espaço
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Instructions Modal */}
      {showInstructions && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80">
          <div className="bg-[#0A0A0A] rounded-2xl p-6 max-w-md w-full border border-white/10 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <h4 className="text-xl font-bold text-white">
                {isIOS ? 'Como instalar no iPhone' : isAndroid ? 'Como instalar no Android' : 'Como instalar o App'}
              </h4>
              <button 
                onClick={() => setShowInstructions(false)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            {isIOS ? (
              // iOS Instructions
              <ol className="space-y-4 text-gray-300">
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#00FF94]/20 text-[#00FF94] flex items-center justify-center text-sm font-bold">1</span>
                  <span>Abra este site no <strong className="text-white">Safari</strong></span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#00FF94]/20 text-[#00FF94] flex items-center justify-center text-sm font-bold">2</span>
                  <div className="flex items-center gap-2">
                    <span>Toque em </span>
                    <Share2 className="w-5 h-5 text-[#00FF94]" />
                    <span><strong className="text-white">Compartilhar</strong></span>
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#00FF94]/20 text-[#00FF94] flex items-center justify-center text-sm font-bold">3</span>
                  <span>Role e toque em <strong className="text-white">"Adicionar à Tela de Início"</strong></span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#00FF94]/20 text-[#00FF94] flex items-center justify-center text-sm font-bold">4</span>
                  <span>Toque em <strong className="text-white">"Adicionar"</strong></span>
                </li>
              </ol>
            ) : isAndroid ? (
              // Android Instructions
              <ol className="space-y-4 text-gray-300">
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#00FF94]/20 text-[#00FF94] flex items-center justify-center text-sm font-bold">1</span>
                  <div className="flex items-center gap-2">
                    <span>Abra no <strong className="text-white">Chrome</strong></span>
                    <Chrome className="w-5 h-5 text-[#00FF94]" />
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#00FF94]/20 text-[#00FF94] flex items-center justify-center text-sm font-bold">2</span>
                  <span>Toque nos <strong className="text-white">3 pontos</strong> (⋮) no canto superior direito</span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#00FF94]/20 text-[#00FF94] flex items-center justify-center text-sm font-bold">3</span>
                  <span>Toque em <strong className="text-white">"Adicionar à tela inicial"</strong> ou <strong className="text-white">"Instalar aplicativo"</strong></span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#00FF94]/20 text-[#00FF94] flex items-center justify-center text-sm font-bold">4</span>
                  <span>Confirme tocando em <strong className="text-white">"Instalar"</strong></span>
                </li>
              </ol>
            ) : (
              // Desktop Instructions
              <ol className="space-y-4 text-gray-300">
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#00FF94]/20 text-[#00FF94] flex items-center justify-center text-sm font-bold">1</span>
                  <div className="flex items-center gap-2">
                    <span>Abra no <strong className="text-white">Chrome</strong> ou <strong className="text-white">Edge</strong></span>
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#00FF94]/20 text-[#00FF94] flex items-center justify-center text-sm font-bold">2</span>
                  <span>Procure o ícone de <strong className="text-white">instalar</strong> (⊕) na barra de endereço</span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#00FF94]/20 text-[#00FF94] flex items-center justify-center text-sm font-bold">3</span>
                  <span>Clique e confirme a instalação</span>
                </li>
              </ol>
            )}

            <Button
              onClick={() => setShowInstructions(false)}
              className="w-full mt-6 bg-[#00FF94] text-black hover:bg-[#00CC76]"
            >
              Entendi!
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DownloadAppSection;
